// Turkey Hunt: a retro arcade quiz shooter played on a cabinet in the arcade
// hall. The pixel field (sky, farm, turkeys, effects) is a 480x270 canvas
// drawn by TurkeyEngine; every word (HUD, question, answers, results) is DOM.
// The bezel, HUD, question box, answer panels and EXIT come from the shared
// arcade kit (../arcade/Kit) so every cabinet looks like one machine family.
// Questions come from the course's quiz beat (questions.ts) and the session is
// reported through the same endpoints as the debrief quiz (report.ts).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useSim } from "../../state/store";
import { audio } from "../../lib/audio";
import type { ScreenBox, TurkeyHuntConfig } from "../../scenario/types";
import {
  ArcadeBezel,
  ArcadeButton,
  ArcadeHud,
  AnswerPanels,
  CrtOverlay,
  ExitButton,
  QuestionBox,
  bigRects,
  cabinetRect,
  useEscapeExit,
  useViewport,
  type PanelState,
} from "../arcade/Kit";
import { exitArcadeDevice, makeResult, saveResult, PASS_RATIO, type CabinetResult } from "../arcade/results";
import { FrogWarning, useMoveOnLabel } from "../arcade/Outcome";
import { TurkeyEngine } from "./engine";
import { W, H } from "./pixel";
import { loadBank, sessionQuestions, type GameQuestion, type QuestionBank } from "./questions";
import { reportSession, type AnswerRecord, type AnswerResult } from "./report";
import { sfx } from "./sfx";
import "./turkeyhunt.css";
import { tx, txr } from "../../lib/tx";

type Phase =
  | "attract"
  | "credit"
  | "expanding"
  | "menu"
  | "ready"
  | "live"
  | "result"
  | "end"
  | "collapsing";

type Mode = "learning" | "arcade";

interface Props {
  config: TurkeyHuntConfig;
  onDone: () => void;
  screenBox: ScreenBox | null;
}

interface Floater {
  id: number;
  xPct: number;
  yPct: number;
  text: string;
  tone: "good" | "bad" | "combo";
}

interface LastAnswer {
  q: GameQuestion;
  rec: AnswerRecord;
  pickedIdx: number | null;
  bonus: number;
  comboApplied: boolean;
}

interface EndInfo {
  gameOver: boolean;
  heartBonus: number;
  totalMs: number;
  shotAcc: number | null;
  stars: number;
  correct: number;
  total: number;
  topics: string[];
  cabinet: CabinetResult;
}

const SHOT_COOLDOWN_MS = 280;
const GOBBLE_GAP_MS = 3000;
const MAX_LIVES = 3;
const EMPTY_BANK: QuestionBank = { beatId: "", source: [], valid: [], rejected: [] };

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const pad = (n: number, w: number) => String(Math.max(0, Math.floor(n))).padStart(w, "0");
const fmtTime = (ms: number) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${pad(s % 60, 2)}`;
};
const newSessionId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `th-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function Star({ on }: { on: boolean }) {
  const px = [
    [4, 0],
    [3, 1], [4, 1], [5, 1],
    [0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2], [8, 2],
    [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3],
    [2, 4], [3, 4], [4, 4], [5, 4], [6, 4],
    [2, 5], [3, 5], [5, 5], [6, 5],
    [1, 6], [2, 6], [6, 6], [7, 6],
  ];
  return (
    <svg className={`th-star ${on ? "is-on" : ""}`} viewBox="0 0 9 7" aria-hidden="true">
      {px.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1.02} height={1.02} />
      ))}
    </svg>
  );
}

export function TurkeyHunt({ config, onDone, screenBox }: Props) {
  const scenario = useSim((s) => s.scenario);
  const bank = useMemo(
    () => (scenario ? loadBank(scenario, config.quizBeatId) : EMPTY_BANK),
    [scenario, config.quizBeatId],
  );
  const questionCount = clamp(Math.round(config.questionCount ?? 10), 1, 30);
  const timerMs = clamp(config.timerS ?? 20, 5, 120) * 1000;
  const title = config.title ?? "TURKEY HUNT";

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const reticleRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<TurkeyEngine | null>(null);
  const timers = useRef<number[]>([]);
  /** A run was started and has not reached its end screen yet. */
  const runOpen = useRef(false);
  const moveOn = useMoveOnLabel();

  const vp = useViewport();
  const [phase, setPhase] = useState<Phase>("attract");
  const [mode, setMode] = useState<Mode>(config.mode === "arcade" ? "arcade" : "learning");
  const [soundOn, setSoundOn] = useState(() => sfx.isEnabled());
  const [reduced, setReduced] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [questions, setQuestions] = useState<GameQuestion[]>([]);
  const [qi, setQi] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(MAX_LIVES);
  const [streak, setStreak] = useState(0);
  const [remaining, setRemaining] = useState(timerMs);
  const [last, setLast] = useState<LastAnswer | null>(null);
  const [endInfo, setEndInfo] = useState<EndInfo | null>(null);
  const [floaters, setFloaters] = useState<Floater[]>([]);
  const [flash, setFlash] = useState<"good" | "bad" | null>(null);
  const [kick, setKick] = useState(0);

  // Mutable game state read by event handlers and timers.
  const g = useRef({
    phase: "attract" as Phase,
    mode: "learning" as Mode,
    questions: [] as GameQuestion[],
    qi: 0,
    roundStart: 0,
    shots: 0,
    misses: 0,
    lastShot: 0,
    resolved: true,
    sessionStart: 0,
    sessionId: "",
    totalShots: 0,
    turkeyHits: 0,
    streak: 0,
    maxCombo: 0,
    lives: MAX_LIVES,
    score: 0,
    records: [] as AnswerRecord[],
    reported: false,
    lastTick: -1,
    floaterId: 0,
    lastGobble: 0,
  });
  g.current.phase = phase;
  g.current.mode = mode;

  useEffect(() => {
    sfx.configure({ shotUrl: config.shotUrl, gobbleUrl: config.gobbleUrl });
  }, [config.shotUrl, config.gobbleUrl]);

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  }, []);

  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    },
    [],
  );

  // Engine lifecycle: the attract loop starts as soon as the cabinet is shown.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const e = new TurkeyEngine(c);
    engineRef.current = e;
    e.setReducedMotion(reduced);
    // A turkey call when one pops into view, only while a round is live.
    e.onAppear = () => {
      const st = g.current;
      if (st.phase !== "live" || st.resolved) return;
      const now = performance.now();
      if (now - st.lastGobble < GOBBLE_GAP_MS) return;
      st.lastGobble = now;
      sfx.gobble();
    };
    e.setupRound(0.15, { badges: false });
    e.beginRound();
    e.start();
    return () => {
      e.onAppear = null;
      e.stop();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    engineRef.current?.setReducedMotion(reduced);
  }, [reduced]);

  // Keep the attract demo populated.
  useEffect(() => {
    if (phase !== "attract" && phase !== "credit" && phase !== "menu") return;
    const id = window.setInterval(() => {
      const e = engineRef.current;
      if (e && !e.hasTurkeys) {
        e.setupRound(0.15, { badges: false });
        e.beginRound();
      }
    }, 900);
    return () => window.clearInterval(id);
  }, [phase]);

  const big = phase !== "attract" && phase !== "credit" && phase !== "collapsing";

  // Duck the hall ambience while the game owns the screen.
  useEffect(() => {
    if (!big) return;
    audio.holdMediaDuck("turkeyhunt");
    return () => audio.releaseMediaDuck("turkeyhunt");
  }, [big]);

  // ---------------------------------------------------------------- geometry
  const full = useMemo(() => bigRects(vp), [vp]);
  const cabRect = useMemo(() => cabinetRect(vp, screenBox), [screenBox, vp]);

  const stageRect = big ? full.glass : cabRect;
  const animating = phase === "expanding" || phase === "collapsing";
  const stageStyle: CSSProperties = {
    left: stageRect.left,
    top: stageRect.top,
    width: stageRect.width,
    height: stageRect.height,
  };

  // ------------------------------------------------------------- floaters
  const addFloater = useCallback(
    (x: number, y: number, text: string, tone: Floater["tone"]) => {
      const id = ++g.current.floaterId;
      setFloaters((f) => [...f, { id, xPct: (x / W) * 100, yPct: (y / H) * 100, text, tone }]);
      later(() => setFloaters((f) => f.filter((k) => k.id !== id)), 1100);
    },
    [later],
  );

  // ------------------------------------------------------------- flow
  const insertCoin = () => {
    sfx.unlock();
    sfx.coin();
    setPhase("credit");
  };

  const pressStart = () => {
    sfx.unlock();
    sfx.start();
    setPhase("expanding");
    later(() => setPhase("menu"), reduced ? 60 : 720);
  };

  // EXIT: allowed in every phase. A started session is reported as unfinished.
  const leave = useCallback(() => {
    const st = g.current;
    if (st.phase === "collapsing") return;
    sfx.unlock();
    if (st.records.length && !st.reported) {
      st.reported = true;
      void reportSession(bank.beatId, bank.source, st.records, {
        sessionId: st.sessionId,
        mode: st.mode,
        finalScore: st.score,
        correct: st.records.filter((r) => r.result === "correct").length,
        total: st.questions.length,
        shotAccuracy: st.totalShots ? st.turkeyHits / st.totalShots : 0,
        maxCombo: st.maxCombo,
        totalTimeMs: Math.round(performance.now() - st.sessionStart),
        stars: 0,
        completed: false,
      });
    }
    // A run left before its end still counts (as not passed) when this
    // cabinet has no result yet; a saved result is never replaced by it.
    if (runOpen.current) {
      runOpen.current = false;
      const total = st.questions.length;
      const correct = st.records.filter((r) => r.result === "correct").length;
      const missed = st.records.filter((r) => r.result !== "correct");
      saveResult(
        makeResult("turkeyhunt", {
          rawScore: st.score,
          correct,
          incorrect: total - correct,
          total,
          completed: false,
          mistakes: missed.map((r) => {
            const qq = st.questions.find((x) => x.id === r.questionId);
            return { topic: r.topic, lesson: qq?.explanation || `Review: ${r.topic}` };
          }),
        }),
        { finished: false },
      );
    }
    if (st.phase === "attract" || st.phase === "credit") {
      exitArcadeDevice(onDone, "turkeyhunt");
      return;
    }
    st.resolved = true;
    engineRef.current?.clear();
    setPhase("collapsing");
    later(() => exitArcadeDevice(onDone, "turkeyhunt"), reduced ? 60 : 720);
  }, [bank, later, onDone, reduced]);

  useEscapeExit(leave, phase !== "collapsing");

  const toReady = useCallback(
    (i: number) => {
      g.current.qi = i;
      g.current.resolved = true;
      setQi(i);
      setRemaining(timerMs);
      setLast(null);
      engineRef.current?.clear();
      setPhase("ready");
    },
    [timerMs],
  );

  const startGame = () => {
    sfx.unlock();
    const qs = sessionQuestions(bank, questionCount);
    if (!qs.length) return;
    const st = g.current;
    st.questions = qs;
    st.sessionId = newSessionId();
    st.sessionStart = performance.now();
    st.totalShots = 0;
    st.turkeyHits = 0;
    st.streak = 0;
    st.maxCombo = 0;
    st.lives = MAX_LIVES;
    st.score = 0;
    st.records = [];
    st.reported = false;
    runOpen.current = true;
    setQuestions(qs);
    setScore(0);
    setLives(MAX_LIVES);
    setStreak(0);
    setEndInfo(null);
    toReady(0);
  };

  const startRound = () => {
    const st = g.current;
    if (st.phase !== "ready") return;
    sfx.unlock();
    sfx.start();
    const n = st.questions.length;
    const d = n > 1 ? st.qi / (n - 1) : 0;
    const e = engineRef.current;
    e?.setupRound(d);
    e?.beginRound();
    st.roundStart = performance.now();
    st.shots = 0;
    st.misses = 0;
    st.lastShot = 0;
    st.lastTick = -1;
    st.lastGobble = 0;
    st.resolved = false;
    setRemaining(timerMs);
    setPhase("live");
  };

  const finish = useCallback(
    (gameOver: boolean) => {
      const st = g.current;
      runOpen.current = false;
      const total = st.questions.length;
      const correct = st.records.filter((r) => r.result === "correct").length;
      const heartBonus = gameOver ? 0 : st.lives * 50;
      st.score += heartBonus;
      const ratio = total ? correct / total : 0;
      const stars = ratio >= 0.9 ? 3 : ratio >= 0.7 ? 2 : ratio >= 0.4 ? 1 : 0;
      const totalMs = Math.round(performance.now() - st.sessionStart);
      const shotAcc = st.totalShots ? st.turkeyHits / st.totalShots : null;
      const missed = st.records.filter((r) => r.result !== "correct");
      const topics = Array.from(new Set(missed.map((r) => r.topic)));
      const cabinet = makeResult("turkeyhunt", {
        rawScore: st.score,
        correct,
        incorrect: total - correct,
        total,
        completed: true,
        mistakes: missed.map((r) => {
          const qq = st.questions.find((x) => x.id === r.questionId);
          return { topic: r.topic, lesson: qq?.explanation || `Review: ${r.topic}` };
        }),
      });
      saveResult(cabinet);
      const info: EndInfo = { gameOver, heartBonus, totalMs, shotAcc, stars, correct, total, topics, cabinet };
      setScore(st.score);
      setEndInfo(info);
      engineRef.current?.clear();
      setPhase("end");
      if (gameOver) sfx.wrong();
      else sfx.fanfare();
      if (!st.reported) {
        st.reported = true;
        void reportSession(bank.beatId, bank.source, st.records, {
          sessionId: st.sessionId,
          mode: st.mode,
          finalScore: st.score,
          correct,
          total,
          shotAccuracy: shotAcc ?? 0,
          maxCombo: st.maxCombo,
          totalTimeMs: totalMs,
          stars,
          completed: !gameOver,
        });
      }
    },
    [bank],
  );

  const resolveAnswer = useCallback(
    (pickedIdx: number | null, at?: { x: number; y: number }) => {
      const st = g.current;
      if (st.phase !== "live" || st.resolved) return;
      st.resolved = true;
      const q = st.questions[st.qi];
      if (!q) return;
      const elapsed = performance.now() - st.roundStart;
      const left = Math.max(0, timerMs - elapsed);
      const choice = pickedIdx === null ? null : q.choices[pickedIdx];
      const result: AnswerResult = !choice ? "unanswered" : choice.id === q.correctId ? "correct" : "incorrect";
      let points = 0;
      let bonus = 0;
      let comboApplied = false;
      const e = engineRef.current;
      const pos = at ?? (pickedIdx !== null ? e?.turkeyPos(pickedIdx + 1) : null) ?? { x: W / 2, y: H / 2 };

      if (result === "correct") {
        bonus = Math.round((50 * left) / timerMs);
        comboApplied = st.streak >= 3;
        points = (100 + bonus) * (comboApplied ? 2 : 1);
        st.streak += 1;
        st.maxCombo = Math.max(st.maxCombo, st.streak);
        st.score += points;
        sfx.hit();
        if (comboApplied) sfx.combo();
        addFloater(pos.x, pos.y, `+${points}`, comboApplied ? "combo" : "good");
        setFlash("good");
      } else {
        st.streak = 0;
        st.lives = Math.max(0, st.lives - 1);
        if (result === "unanswered") sfx.timeout();
        else {
          sfx.wrong();
          addFloater(pos.x, pos.y, "0", "bad");
        }
        setFlash("bad");
      }
      later(() => setFlash(null), 260);
      e?.resolve(pickedIdx === null ? null : pickedIdx + 1, result === "correct");

      const rec: AnswerRecord = {
        questionId: q.id,
        position: q.position,
        topic: q.topic,
        selected: choice ? choice.id : null,
        result,
        responseMs: Math.round(Math.min(elapsed, timerMs)),
        attempts: choice ? 1 : 0,
        missedShots: st.misses,
        points,
      };
      st.records.push(rec);
      setScore(st.score);
      setStreak(st.streak);
      setLives(st.lives);
      setRemaining(left);
      setLast({ q, rec, pickedIdx, bonus, comboApplied });
      later(() => {
        if (g.current.phase === "live") setPhase("result");
      }, reduced ? 350 : 1150);
    },
    [addFloater, later, reduced, timerMs],
  );

  const next = useCallback(() => {
    const st = g.current;
    if (st.phase !== "result") return;
    sfx.unlock();
    if (st.mode === "arcade" && st.lives <= 0) {
      finish(true);
      return;
    }
    if (st.qi + 1 >= st.questions.length) {
      finish(false);
      return;
    }
    toReady(st.qi + 1);
  }, [finish, toReady]);

  // Round timer.
  useEffect(() => {
    if (phase !== "live") return;
    const id = window.setInterval(() => {
      const st = g.current;
      if (st.resolved) return;
      const left = Math.max(0, timerMs - (performance.now() - st.roundStart));
      setRemaining(left);
      const sec = Math.ceil(left / 1000);
      if (sec <= 5 && sec > 0 && sec !== st.lastTick) {
        st.lastTick = sec;
        sfx.tick();
      }
      const e = engineRef.current;
      if (e && !e.hasTurkeys) {
        const n = st.questions.length;
        e.setupRound(n > 1 ? st.qi / (n - 1) : 0);
        e.beginRound();
      }
      if (left <= 0) resolveAnswer(null);
    }, 100);
    return () => window.clearInterval(id);
  }, [phase, resolveAnswer, timerMs]);

  // ------------------------------------------------------------- input
  const shoot = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const st = g.current;
    if (st.phase !== "live" || st.resolved) return;
    if ((ev.target as HTMLElement).closest("button")) return;
    ev.preventDefault();
    const now = performance.now();
    if (now - st.lastShot < SHOT_COOLDOWN_MS) return;
    st.lastShot = now;
    const c = canvasRef.current;
    const e = engineRef.current;
    if (!c || !e) return;
    const r = c.getBoundingClientRect();
    const x = ((ev.clientX - r.left) / r.width) * W;
    const y = ((ev.clientY - r.top) / r.height) * H;
    st.shots += 1;
    st.totalShots += 1;
    sfx.shot();
    setKick((k) => k + 1);
    const res = e.shoot(x, y, ev.pointerType === "touch");
    if (res.hit !== null) {
      st.turkeyHits += 1;
      resolveAnswer(res.hit - 1, { x: res.x, y: res.y - 10 });
    } else {
      st.misses += 1;
      sfx.miss();
    }
  };

  const moveReticle = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const el = reticleRef.current;
    const stage = stageRef.current;
    if (!el || !stage) return;
    if (ev.pointerType === "touch") {
      el.style.opacity = "0";
      return;
    }
    const r = stage.getBoundingClientRect();
    el.style.opacity = "1";
    el.style.transform = `translate(${ev.clientX - r.left}px, ${ev.clientY - r.top}px)`;
  };

  const hideReticle = () => {
    if (reticleRef.current) reticleRef.current.style.opacity = "0";
  };

  // Keyboard: 1/2/3 answer, Enter/Space advance. Escape is useEscapeExit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const st = g.current;
      const k = e.key;
      let used = true;
      if (st.phase === "live" && (k === "1" || k === "2" || k === "3")) {
        resolveAnswer(Number(k) - 1);
      } else if (k === "Enter" || k === " ") {
        if (st.phase === "attract") insertCoin();
        else if (st.phase === "credit") pressStart();
        else if (st.phase === "ready") startRound();
        else if (st.phase === "result") next();
        else used = false;
      } else {
        used = false;
      }
      if (used) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  const toggleSound = () => {
    const on = !soundOn;
    sfx.setEnabled(on);
    setSoundOn(on);
    if (on) {
      sfx.unlock();
      sfx.coin();
    }
  };

  // ------------------------------------------------------------- render
  const q = questions[qi];
  const secLeft = Math.ceil(remaining / 1000);
  const timePct = clamp((remaining / timerMs) * 100, 0, 100);
  const hud = phase === "ready" || phase === "live" || phase === "result";
  const answered = phase === "result" || (phase === "live" && last !== null);
  const comboReady = streak >= 3;
  const noQuestions = bank.valid.length === 0;
  const passPct = Math.round(PASS_RATIO.turkeyhunt * 100);

  const panelState = (i: number): PanelState => {
    if (!answered || !last || !q) return "";
    if (q.choices[i]?.id === last.q.correctId) return "correct";
    if (last.pickedIdx === i) return "wrong";
    return "dim";
  };

  const ctlTop = Math.min(cabRect.top + cabRect.height + 14, vp.h - 64);

  return (
    <div className={`th-root arc-root th-${phase}${reduced ? " th-reduced" : ""}`}>
      <div className={`th-scrim${big ? " is-on" : ""}`} />
      <ArcadeBezel frame={full.frame} side={full.side} visible={big && phase !== "expanding"} />

      <div
        ref={stageRef}
        className={`th-stage arc-glass${big ? " is-big" : " is-cab"}${animating ? " th-anim" : ""}${phase === "live" ? " is-live" : ""}`}
        style={stageStyle}
        onPointerDown={shoot}
        onPointerMove={moveReticle}
        onPointerLeave={hideReticle}
      >
        <canvas ref={canvasRef} className="th-canvas" aria-hidden="true" />
        <CrtOverlay />
        {flash && <div className={`th-flash th-flash--${flash}`} aria-hidden="true" />}

        {/* Cabinet attract screen */}
        {(phase === "attract" || phase === "credit" || phase === "collapsing") && (
          <div className="th-attract">
            <div className="th-logo">{title}</div>
            {phase === "attract" && <div className="th-blink">{tx("INSERT COIN")}</div>}
            {phase === "credit" && (
              <>
                <div className="th-credit">{tx("CREDIT 1")}</div>
                <div className="th-blink">{tx("PRESS START")}</div>
              </>
            )}
            {phase === "collapsing" && <div className="th-credit">{tx("THANKS FOR PLAYING")}</div>}
          </div>
        )}

        {/* Menu */}
        {phase === "menu" && (
          <div className="arc-panel-wrap">
            <div className="arc-panel is-wide th-kmenu" role="dialog" aria-label={tx("{title} menu", { title })}>
              <div className="arc-title">{title}</div>
              {noQuestions ? (
                <p className="arc-text">{tx("This cabinet has no questions loaded right now.")}</p>
              ) : (
                <>
                  {config.mode !== "arcade" && (
                  <div className="th-kmodes" role="radiogroup" aria-label={tx("Game mode")}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={mode === "learning"}
                      className={`th-kmode${mode === "learning" ? " is-on" : ""}`}
                      onClick={() => setMode("learning")}
                    >
                      <span className="th-kmode__t">{tx("LEARNING")}</span>
                      <span className="th-kmode__d">{tx("Play every question. Lives left at the end add bonus points.")}</span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={mode === "arcade"}
                      className={`th-kmode${mode === "arcade" ? " is-on" : ""}`}
                      onClick={() => setMode("arcade")}
                    >
                      <span className="th-kmode__t">{tx("ARCADE")}</span>
                      <span className="th-kmode__d">{tx("Three lives. A wrong answer or a time out costs one.")}</span>
                    </button>
                  </div>
                  )}
                  <ul className="th-khow">
                    {config.mode === "arcade" && (
                      <li>{tx("You have three lives. A wrong answer or a time out costs one. Lose all three and the game is over.")}</li>
                    )}
                    <li>{tx("Read the question, then press START ROUND.")}</li>
                    <li>{tx("Shoot the turkey whose number matches the right answer, or press 1, 2 or 3.")}</li>
                    <li>{tx("Turkeys only stay in view for a few seconds. Faster answers score more.")}</li>
                    <li>{txr("Get {passPct}% right to clear this cabinet.", { passPct })}</li>
                  </ul>
                </>
              )}
              <div className="arc-actions">
                <button type="button" className={`arc-mini${soundOn ? " is-on" : ""}`} onClick={toggleSound}>
                  {txr("SOUND: {v}", { v: soundOn ? tx("ON") : tx("OFF") })}
                </button>
                <button type="button" className={`arc-mini${reduced ? " is-on" : ""}`} onClick={() => setReduced((r) => !r)}>
                  {txr("REDUCED MOTION: {v}", { v: reduced ? tx("ON") : tx("OFF") })}
                </button>
              </div>
              {!noQuestions && (
                <div className="arc-actions">
                  <ArcadeButton primary onClick={startGame}>
                    {tx("START GAME")}
                  </ArcadeButton>
                </div>
              )}
            </div>
          </div>
        )}

        {/* In-round HUD */}
        {hud && q && (
          <>
            <ArcadeHud
              score={score}
              wave={{ label: "WAVE", value: `${qi + 1}/${questions.length}` }}
              lives={{ n: lives, max: MAX_LIVES }}
              extra={
                <>
                  {comboReady && <span className="th-kcombo">{tx("2X COMBO")}</span>}
                  <span className={`th-kclock${phase === "live" && secLeft <= 5 ? " is-low" : ""}`}>
                    {txr("TIME {v}", { v: pad(phase === "ready" ? timerMs / 1000 : secLeft, 2) })}
                  </span>
                  <button
                    type="button"
                    className="arc-mini"
                    onClick={toggleSound}
                    aria-label={soundOn ? tx("Turn sound off") : tx("Turn sound on")}
                  >
                    {soundOn ? tx("SND ON") : tx("SND OFF")}
                  </button>
                </>
              }
            />

            <QuestionBox title={tx("QUESTION {v} OF {length}", { v: qi + 1, length: questions.length })} subtitle={q.prompt}>
              <div className="th-kbar" aria-hidden="true">
                <span style={{ width: `${phase === "ready" ? 100 : timePct}%` }} />
              </div>
            </QuestionBox>

            <AnswerPanels items={q.choices.map((c) => ({ key: c.id, label: c.label }))} state={panelState} />
          </>
        )}

        {phase === "ready" && q && (
          <div className="arc-panel-wrap th-kmid">
            <div className="arc-panel">
              <div className="arc-sub">{qi === 0 ? tx("GET READY") : tx("ROUND {v}", { v: qi + 1 })}</div>
              <p className="arc-text">{tx("Read the question and the three answers. The timer starts when you press START ROUND.")}</p>
              <ArcadeButton primary onClick={startRound}>
                {tx("START ROUND")}
              </ArcadeButton>
            </div>
          </div>
        )}

        {phase === "result" && last && (
          <div className="arc-panel-wrap th-kmid">
            <div className="arc-panel">
              <div className="arc-title th-ktitle">
                {last.rec.result === "correct" ? tx("CORRECT!") : last.rec.result === "incorrect" ? tx("NOT QUITE") : tx("TIME'S UP")}
              </div>
              {last.rec.result === "correct" ? (
                <div className="arc-sub">
                  +100{last.bonus > 0 ? tx(" +{bonus} SPEED", { bonus: last.bonus }) : ""}
                  {last.comboApplied ? tx(" X2 COMBO") : ""} = {last.rec.points}
                </div>
              ) : (
                <div className="arc-sub">
                  {txr("THE RIGHT ANSWER WAS {v}: {label}", { v: last.q.choices.findIndex((c) => c.id === last.q.correctId) + 1, label: last.q.choices.find((c) => c.id === last.q.correctId)?.label })}
                </div>
              )}
              {last.q.explanation && <p className="arc-text">{last.q.explanation}</p>}
              <ArcadeButton primary onClick={next}>
                {(mode === "arcade" && lives <= 0) || qi + 1 >= questions.length ? tx("SEE RESULTS") : tx("NEXT QUESTION")}
              </ArcadeButton>
            </div>
          </div>
        )}

        {phase === "end" && endInfo && (
          <div className="arc-panel-wrap">
            <div className="arc-panel is-wide">
              <div className="arc-title">{endInfo.gameOver ? tx("GAME OVER") : tx("ROUND COMPLETE")}</div>
              {endInfo.cabinet.passedCabinetThreshold && (
                <div className="th-stars">
                  {[0, 1, 2].map((i) => (
                    <Star key={i} on={i < endInfo.stars} />
                  ))}
                </div>
              )}
              <div className="th-kfinal">
                <span className="arc-label">{tx("FINAL SCORE")}</span>
                <span className="th-kfinal__v">{pad(score, 6)}</span>
              </div>
              <div className="th-kstats">
                <div>
                  <span className="arc-label">{tx("CORRECT")}</span>
                  <b>
                    {endInfo.correct}/{endInfo.total}
                  </b>
                </div>
                <div>
                  <span className="arc-label">{tx("ACCURACY")}</span>
                  <b>{endInfo.shotAcc === null ? "-" : `${Math.round(endInfo.shotAcc * 100)}%`}</b>
                </div>
                <div>
                  <span className="arc-label">{tx("MAX COMBO")}</span>
                  <b>{g.current.maxCombo}</b>
                </div>
                <div>
                  <span className="arc-label">{tx("TIME")}</span>
                  <b>{fmtTime(endInfo.totalMs)}</b>
                </div>
              </div>
              {endInfo.heartBonus > 0 && <div className="arc-text">{txr("Lives bonus: +{heartBonus}", { heartBonus: endInfo.heartBonus })}</div>}
              <div className="arc-sub">
                {endInfo.cabinet.passedCabinetThreshold
                  ? tx("CABINET CLEARED")
                  : tx("CABINET NOT CLEARED: {passPct}% NEEDED", { passPct })}
              </div>
              <div className="th-kreview">
                <span className="arc-label">{tx("TOPICS TO REVIEW")}</span>
                {endInfo.topics.length ? (
                  <ul>
                    {endInfo.topics.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="arc-text">{tx("None. Every question answered correctly.")}</p>
                )}
              </div>
              {!endInfo.cabinet.passedCabinetThreshold && <FrogWarning />}
              <div className="arc-actions">
                <ArcadeButton primary={!endInfo.cabinet.passedCabinetThreshold} onClick={startGame}>
                  {endInfo.cabinet.passedCabinetThreshold ? tx("PLAY AGAIN") : tx("TRY AGAIN")}
                </ArcadeButton>
                <ArcadeButton primary={endInfo.cabinet.passedCabinetThreshold} onClick={leave}>
                  {moveOn}
                </ArcadeButton>
              </div>
            </div>
          </div>
        )}

        {floaters.map((f) => (
          <div
            key={f.id}
            className={`th-float th-float--${f.tone}`}
            style={{ left: `${f.xPct}%`, top: `${f.yPct}%` }}
          >
            {f.text}
          </div>
        ))}

        {phase === "live" && (
          <div ref={reticleRef} className="th-reticle" aria-hidden="true">
            <svg key={kick} className="th-reticle__svg" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="10" />
              <line x1="16" y1="1" x2="16" y2="9" />
              <line x1="16" y1="23" x2="16" y2="31" />
              <line x1="1" y1="16" x2="9" y2="16" />
              <line x1="23" y1="16" x2="31" y2="16" />
              <rect x="15" y="15" width="2" height="2" />
            </svg>
          </div>
        )}
      </div>

      {phase !== "collapsing" && <ExitButton onExit={leave} />}

      {(phase === "attract" || phase === "credit") && (
        <div className="th-cabctl" style={{ top: ctlTop }}>
          {phase === "attract" ? (
            <button type="button" className="th-btn th-btn--primary" onClick={insertCoin}>
              {tx("Insert coin")}
            </button>
          ) : (
            <button type="button" className="th-btn th-btn--primary" onClick={pressStart}>
              {tx("Press start")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
